/**
 * Static Splitwise-label → app-taxonomy table. Versioned with
 * `EXPENSE_TAXONOMY_VERSION` so a taxonomy change forces this table to be
 * revisited; the review screen lets the user override any single mapping,
 * and the override travels in the payload as the row's rewritten path.
 */
import {
  EXPENSE_TAXONOMY,
  EXPENSE_TAXONOMY_VERSION,
} from '../categoryPrediction/taxonomy';
import type { ExpenseCategory } from '../categoryPrediction/types';

export interface MappedCategory {
  category: ExpenseCategory;
  subcategory?: string;
}

export const SPLITWISE_CATEGORY_MAP_VERSION: number = EXPENSE_TAXONOMY_VERSION;

export const SPLITWISE_CATEGORY_MAP: Readonly<
  Partial<Record<string, Readonly<MappedCategory>>>
> = {
  general: { category: 'other' },
  groceries: { category: 'food', subcategory: 'groceries' },
  'dining out': { category: 'food', subcategory: 'restaurant' },
  liquor: { category: 'drinks', subcategory: 'alcohol' },
  'food and drink - other': { category: 'food' },
  plane: { category: 'transport', subcategory: 'flight' },
  'bus/train': { category: 'transport', subcategory: 'train' },
  taxi: { category: 'transport', subcategory: 'taxi' },
  car: { category: 'transport', subcategory: 'car' },
  'gas/fuel': { category: 'transport', subcategory: 'fuel' },
  parking: { category: 'transport', subcategory: 'parking' },
  hotel: { category: 'accommodation', subcategory: 'hotel' },
  services: { category: 'shopping', subcategory: 'services' },
  gifts: { category: 'shopping', subcategory: 'gifts' },
  clothing: { category: 'shopping', subcategory: 'clothes' },
  electricity: { category: 'utilities', subcategory: 'electricity' },
  'entertainment - other': { category: 'entertainment' },
  sports: { category: 'entertainment', subcategory: 'sports' },
  'medical expenses': { category: 'healthcare', subcategory: 'doctor' },
  insurance: { category: 'healthcare', subcategory: 'insurance' },
  education: { category: 'education' },
  childcare: { category: 'other' },
  'life - other': { category: 'other' },
};

/**
 * `hasOwn` rather than `in`/bare indexing: the label comes from a user-supplied
 * CSV column, so `constructor` or `__proto__` would otherwise resolve up the
 * prototype chain and hand back something that is not a MappedCategory.
 */
function lookup(raw: string): Readonly<MappedCategory> | undefined {
  const key = raw.trim().toLowerCase();
  return Object.hasOwn(SPLITWISE_CATEGORY_MAP, key)
    ? SPLITWISE_CATEGORY_MAP[key]
    : undefined;
}

export function isMappedSplitwiseCategory(raw: string): boolean {
  return lookup(raw) !== undefined;
}

export function mapSplitwiseCategory(raw: string): Readonly<MappedCategory> {
  return lookup(raw) ?? { category: 'other' };
}

export function formatTaxonomyPath(mapped: MappedCategory): string {
  return mapped.subcategory
    ? `${mapped.category}/${mapped.subcategory}`
    : mapped.category;
}

export function parseTaxonomyPath(path: string): MappedCategory {
  const [rawCategory, rawSubcategory] = path.trim().toLowerCase().split('/');
  if (!Object.hasOwn(EXPENSE_TAXONOMY, rawCategory)) {
    return { category: 'other' };
  }
  const category = rawCategory as ExpenseCategory;
  const subs = EXPENSE_TAXONOMY[category] as readonly string[];
  return rawSubcategory && subs.includes(rawSubcategory)
    ? { category, subcategory: rawSubcategory }
    : { category };
}
